import { FC, useState, useEffect } from "react";
import { serverTimestamp } from "firebase/firestore";
import {
  Box,
  Button,
  Typography,
  Stack,
  TextField,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db, auth } from "../config/firebase"; // auth を使用
import CryptoJS from "crypto-js";

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

if (!ENCRYPTION_KEY) {
  console.error("暗号化キーが設定されていません。REACT_APP_ENCRYPTION_KEY を確認してください。");
}

type Form = {
  name: string;
  memo: string;
  promptId: string;
  isPublic: boolean;
};

export const LinebotEditPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = auth.currentUser?.uid;
  const { control, handleSubmit, setValue, formState: { errors } } = useForm<Form>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [secretId, setSecretId] = useState<string | null>(null);
  const [accessKey, setAccessKey] = useState<string | null>(null);
  const [webhookURL, setWebhookURL] = useState<string | null>(null);
  const [user, setUser] = useState(auth.currentUser);
  const decrypt = (encryptedText: string): string => {
    if (!ENCRYPTION_KEY) {
      throw new Error("暗号化キーが設定されていません。");
    }
    const bytes = CryptoJS.AES.decrypt(encryptedText, ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  };
  useEffect(() => {

    const shouldRedirect = true; // このページはログイン必須
    const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      // グローバルなガード（例：未ログインならログインへ）
      if (!currentUser && shouldRedirect) {
        const currentPath = location.pathname + location.search;
        navigate(`/settings/login?redirect=${encodeURIComponent(currentPath)}&reason=auth_required`);
      }
    });

    return () => unsubscribeAuth();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) {
          throw new Error("編集対象のIDが指定されていません");
        }

        const docRef = doc(db, "linebots", id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          throw new Error("編集対象のデータが存在しません");
        }

        const linebotData = docSnap.data();
        setValue("name", linebotData.name || "");
        setValue("memo", linebotData.memo || "");
        setValue("promptId", linebotData.promptId || "");
        setValue("isPublic", linebotData.isPublic || false);

        // シークレットデータを復号化
        if (linebotData.secretId) {
          setSecretId(decrypt(linebotData.secretId));
        }
        if (linebotData.accessKey) {
          setAccessKey(decrypt(linebotData.accessKey));
        }

        // ✅ Webhook URL を常に表示
        setWebhookURL(`https://linebot.zero-one.llc/lineWebhook?id=${id}`);

        // プロンプトデータを取得
        const promptsRef = collection(db, "prompts");
        const userPromptQuery = query(promptsRef, where("userId", "==", userId));
        const publicPromptQuery = query(promptsRef, where("isPublic", "==", true));

        const [userPromptSnap, publicPromptSnap] = await Promise.all([
          getDocs(userPromptQuery),
          getDocs(publicPromptQuery),
        ]);

        const fetchedPrompts = Array.from(
          new Map(
            [...userPromptSnap.docs, ...publicPromptSnap.docs].map((doc) => [doc.id, { id: doc.id, ...doc.data() }])
          ).values()
        );

        setPrompts(fetchedPrompts);
      } catch (error) {
        console.error("データの取得に失敗しました", error);
        setErrorMessage("データの取得中にエラーが発生しました。再試行してください。");
      }
    };

    fetchData();
  }, [id, userId, setValue]);

  const submit = async (data: Form) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      if (!id) {
        throw new Error("編集対象のIDが指定されていません");
      }

      const docRef = doc(db, "linebots", id);
      await updateDoc(docRef, {
        name: data.name,
        memo: data.memo,
        promptId: data.promptId,
        isPublic: data.isPublic,
        updatedAt: serverTimestamp(),
      });

      navigate("/linebots");
    } catch (error) {
      console.error("Linebotの更新に失敗しました", error);
      setErrorMessage("更新中にエラーが発生しました。再試行してください。");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit(submit)}
      sx={{
        p: 5,
        mt: 4,
        mx: "auto",
        maxWidth: "600px",
        border: "1px dashed grey",
        borderRadius: 2,
        backgroundColor: "#f7f7f7",
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h5" textAlign="center">
          AI Line Botを編集
        </Typography>

        <Controller
          name="name"
          control={control}
          defaultValue=""
          rules={{ required: "Line bot名は必須です" }}
          render={({ field }) => (
            <TextField {...field} label="Line bot名" fullWidth error={!!errors.name} helperText={errors.name?.message} />
          )}
        />

        {/* Webhook URL (デフォルトで表示) */}
        {webhookURL && <TextField label="Webhook URL" value={webhookURL} fullWidth InputProps={{ readOnly: true }} />}

        {/* プロンプト選択 */}
        <Controller
          name="promptId"
          control={control}
          defaultValue=""
          rules={{ required: "Promptの選択は必須です" }}
          render={({ field }) => (
            <TextField {...field} select label="関連付けるPromptを選択" fullWidth error={!!errors.promptId} helperText={errors.promptId?.message}>
              {prompts.map((prompt) => (
                <MenuItem key={prompt.id} value={prompt.id}>
                  {prompt.name || "名前未設定"}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {/* シークレット ID & アクセスキー (パスワード形式) */}
        {secretId && <TextField label="シークレットID" type="password" value={secretId} fullWidth InputProps={{ readOnly: true }} />}
        {accessKey && <TextField label="アクセスキー" type="password" value={accessKey} fullWidth InputProps={{ readOnly: true }} />}

        {/* 公開設定 */}
        <Controller
          name="isPublic"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel control={<Switch {...field} checked={field.value} />} label="公開設定（ON: 一般公開）" />
          )}
        />

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <Button type="submit" variant="contained" fullWidth disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={24} /> : "更新する"}
        </Button>
      </Stack>
    </Box>
  );
};
